import { initializeApp } from 'firebase/app';
import {
  connectAuthEmulator,
  getAuth,
  onAuthStateChanged,
  GoogleAuthProvider,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  signInWithPopup,
} from 'firebase/auth';
import {
  connectFirestoreEmulator,
  getFirestore,
  doc,
  setDoc,
  getDoc,
  collection,
  writeBatch,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  getDocs,
  orderBy,
} from 'firebase/firestore';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  uploadBytesResumable,
} from 'firebase/storage';
import { getDatabase, ref as dbRef, remove, push, onValue, query as rtdbQuery, orderByChild, equalTo, get } from 'firebase/database';
import { getFunctions, connectFunctionsEmulator, httpsCallable } from 'firebase/functions';

const config = {
  apiKey: process.env.REACT_APP_API_KEY,
  authDomain: process.env.REACT_APP_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_DATABASE_URL,
  projectId: process.env.REACT_APP_PROJECT_ID,
  storageBucket: process.env.REACT_APP_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_APP_ID,
  measurementId: process.env.REACT_APP_MEASUREMENT_ID,
};

const app = initializeApp(config);

const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const database = getDatabase(app); // Realtime Database
const functions = getFunctions(app, 'us-central1');

// Centralized exports for all Firebase services and auth methods
export {
  auth,
  db,
  storage,
  database,
  functions,
  // Auth methods
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signOut,
  httpsCallable,
  // Realtime Database methods - export the aliased version
  dbRef,
  onValue,
  rtdbQuery,
  orderByChild,
  equalTo,
  get,
  push,
  remove,
};

// A simple logger that only logs in development
const devLog = (...args) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(...args);
  }
};

// Emulation on localhost -- Comment out this section to view production on localhost
// if (window.location.hostname === 'localhost') {
//   console.log('Connecting to local Firebase emulators');
//   connectAuthEmulator(auth, 'http://localhost:9099');
//   connectFunctionsEmulator(functions, 'localhost', 5001);
//   connectFirestoreEmulator(db, 'localhost', 8080);
// }

// Auth
const provider = new GoogleAuthProvider();
provider.setCustomParameters({ prompt: 'select_account' });
export const signInWithGoogle = () => signInWithPopup(auth, provider);

export const createUserProfileDocument = async (userAuth, additionalData) => {
  if (!userAuth) return;

  const userRef = doc(db, `users/${userAuth.uid}`);
  // The user document is now created exclusively by the `onUserCreated` Cloud Function trigger.
  // This client-side function's only responsibility is to return the reference to the document.
  // This eliminates race conditions and ensures the `createdAt` field is always a server-side timestamp.
  // We can still check if it exists for logging or other client-side logic if needed,
  // but we should not write to it here.
  return userRef;
};

export const updateUserProfile = async (currentUser, personalProfile) => {
  const userRef = doc(db, `users/${currentUser.id}`);
  await updateDoc(userRef, personalProfile);
};

export const uploadUserPhoto = (currentUser, imageFile, onProgress) => {
  return new Promise((resolve, reject) => {
    if (!imageFile) {
      resolve(null);
      return;
    }
    // Using a consistent file path allows for overwriting the profile photo,
    // preventing orphaned files in storage.
    const filePath = `users/${currentUser.id}/profilePhoto.jpg`;
    const storageRefPath = storageRef(storage, filePath);
    
    const uploadTask = uploadBytesResumable(storageRefPath, imageFile);

    uploadTask.on('state_changed',
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        if (onProgress) onProgress(progress);
      },
      (error) => reject(error),
      async () => {
        let photoURL = await getDownloadURL(uploadTask.snapshot.ref);
        const separator = photoURL.includes('?') ? '&' : '?';
        photoURL = `${photoURL}${separator}t=${Date.now()}`;
        const userRef = doc(db, `users/${currentUser.id}`);
        await updateDoc(userRef, { photoURL });
        resolve(photoURL);
      }
    );
  });
};

const updateUserAndBoatsStatus = async (userId, active) => {
  const status = active ? 'activation' : 'deactivation';
  devLog(`STATUS_UPDATE: Starting ${status} for user ID: ${userId}`);
  const batch = writeBatch(db);

  // Update user document
  const userRef = doc(db, 'users', userId);
  batch.update(userRef, { active });
  devLog(`STATUS_UPDATE: Added user users/${userId} to batch for ${status}.`);

  // Update user's boats
  const boatsQuery = query(collection(db, 'boats', userId, 'userBoats'));
  try {
    const boatsSnapshot = await getDocs(boatsQuery);
    if (boatsSnapshot.empty) {
      devLog(`STATUS_UPDATE: No boats found for this user.`);
    } else {
      devLog(`STATUS_UPDATE: Found ${boatsSnapshot.size} boat(s) to update.`);
      boatsSnapshot.forEach((boatDoc) => {
        batch.update(boatDoc.ref, { active });
        devLog(`STATUS_UPDATE: Added boat ${boatDoc.ref.path} to batch.`);
      });
    }

    devLog(`STATUS_UPDATE: Committing batch write...`);
    await batch.commit();
    devLog(`STATUS_UPDATE: Batch commit successful. ${status} complete.`);
  } catch (error) {
    console.error(`STATUS_UPDATE: An error occurred during the ${status} process.`, error);
    throw error; // Re-throw the error so the calling UI can display it
  }
};

export const deactivateUserAndBoats = (userId) => updateUserAndBoatsStatus(userId, false);
export const reactivateUserAndBoats = (userDocRef) => updateUserAndBoatsStatus(userDocRef.id, true);

export const uploadBoatingQualification = async (
  currentUser,
  boatingQualification,
  licenseImageFile
) => {
  const userRef = doc(db, `users/${currentUser.id}`);

  if (licenseImageFile) {
    const imageFile = licenseImageFile;
    const filePath = `captain_licenses/${currentUser.id}/${Date.now()}_${
      imageFile.name
    }`;
    const storageRefPath = storageRef(storage, filePath);
    await uploadBytes(storageRefPath, imageFile);
    const imageUrl = await getDownloadURL(storageRefPath);
    await updateDoc(userRef, {
      ...boatingQualification,
      captainLicense: imageUrl,
    });
  } else {
    await updateDoc(userRef, boatingQualification);
  }
};

export const addEarnings = async (collectionKey, ownerUID, earningsData) => {
  const earningsRef = collection(db, collectionKey, ownerUID, 'userEarnings');
  await addDoc(earningsRef, earningsData);
};

export const addWithdrawal = async (collectionKey, userId, withdrawalData) => {
  const withdrawalRef = collection(db, collectionKey, userId, 'userWithdrawal');
  await addDoc(withdrawalRef, withdrawalData);
};

export const deleteBoat = async (uid, randomId) => {
  const boatRef = doc(db, 'boats', uid, 'userBoats', randomId);
  await deleteDoc(boatRef);
};

export const addContactForm = async (collectionKey, formData) => {
  await addDoc(collection(db, collectionKey), formData);
};

/**
 * Securely creates a booking by calling a Cloud Function that runs a transaction.
 * This prevents double-bookings and ensures data integrity.
 * @param {object} data - The payload, containing `bookingData` and `earningsData`.
 * @returns {Promise<object>} The result from the Cloud Function.
 */
export const createBooking = httpsCallable(functions, 'createBooking');

/**
 * Securely processes a withdrawal by calling a Cloud Function that runs a transaction.
 * @returns {Promise<object>} The result from the Cloud Function.
 */
export const processWithdrawal = httpsCallable(functions, 'processWithdrawal');

// Admin Functions
// Note: All admin functions should be `httpsCallable` for consistency and security.
// They should verify admin privileges on the backend using a helper.
export const getAllBookings = httpsCallable(functions, 'getAllBookings');
export const getAllReviews = httpsCallable(functions, 'getAllReviews');
export const adminDeleteReview = httpsCallable(functions, 'adminDeleteReview');
export const adminCancelBooking = httpsCallable(functions, 'adminCancelBooking');
export const getAllBoats = httpsCallable(functions, 'getAllBoats');
export const adminDeleteBoat = httpsCallable(functions, 'adminDeleteBoat');
export const toggleBoatFeatured = httpsCallable(functions, 'toggleBoatFeatured');
export const updateBoatStatus = httpsCallable(functions, 'updateBoatStatus');
export const adminUpdateUserStatus = httpsCallable(functions, 'adminUpdateUserStatus');
export const getAllEarningsForChart = httpsCallable(functions, 'getAllEarnings');
export const getBookingsForUser = httpsCallable(functions, 'getBookingsForUser');
export const getPaymentHistoryForUser = httpsCallable(functions, 'getPaymentHistoryForUser');
export const backfillEarningStatus = httpsCallable(functions, 'backfillEarningStatus');
export const diagnoseBookingData = httpsCallable(functions, 'diagnoseBookingData');
export const diagnoseOrphanedReviews = httpsCallable(functions, 'diagnoseOrphanedReviews');
export const createProduct = httpsCallable(functions, 'createProduct');
export const updateProduct = httpsCallable(functions, 'updateProduct');
export const deleteProduct = httpsCallable(functions, 'deleteProduct');
export const getAllProducts = httpsCallable(functions, 'getAllProducts');
export const getActiveProducts = httpsCallable(functions, 'getActiveProducts');
export const getContactSubmissions = httpsCallable(functions, 'getContactSubmissions');
export const getMyProducts = httpsCallable(functions, 'getMyProducts');
export const deleteContactSubmission = httpsCallable(functions, 'deleteContactSubmission');
export const updateContactSubmissionStatus = httpsCallable(functions, 'updateContactSubmissionStatus');
export const recordProductPurchase = httpsCallable(functions, 'recordProductPurchase');
export const fulfillFeaturedListing = httpsCallable(functions, 'fulfillFeaturedListing');
export const cancelPurchase = httpsCallable(functions, 'cancelPurchase');
export const getMyPurchases = httpsCallable(functions, 'getMyPurchases');
export const getMyReviewedProductIds = httpsCallable(functions, 'getMyReviewedProductIds');
export const addToFavorites = httpsCallable(functions, 'addToFavorites');
export const removeFromFavorites = httpsCallable(functions, 'removeFromFavorites');
export const getFavoriteBoatIds = httpsCallable(functions, 'getFavoriteBoatIds');
export const getMyFavoriteBoats = httpsCallable(functions, 'getMyFavoriteBoats');
export const getAllPurchases = httpsCallable(functions, 'getAllPurchases');
export const adminFulfillPurchase = httpsCallable(functions, 'adminFulfillPurchase');
export const getUserBookings = httpsCallable(functions, 'getUserBookings');
export const getFollowers = httpsCallable(functions, 'getFollowers');
export const getFollowing = httpsCallable(functions, 'getFollowing');
export const getPublicProfile = httpsCallable(functions, 'getPublicProfile');

/**
 * Calls the setUserRole Cloud Function and then forces a refresh of the
 * current user's ID token if they were the user being modified. This is
 * critical for ensuring the user's new role is reflected immediately
 * on the client.
 * @param {string} uid The UID of the user to modify.
 * @param {string} role The role to set (e.g., 'admin').
 * @param {boolean} value The value for the role (true or false).
 * @returns {Promise<object>} The result from the Cloud Function.
 */
export const setUserRoleAndRefresh = async (uid, role, value) => {
  const setUserRole = httpsCallable(functions, 'setUserRole');
  const result = await setUserRole({ uid, role, value });

  // If the role of the currently signed-in user was changed, force a token refresh.
  if (auth.currentUser && auth.currentUser.uid === uid) {
    await auth.currentUser.getIdToken(true);
  }
  return result;
};

export const updateReview = async (ownerUID, reviewId, reviewData) => {
  const reviewRef = doc(db, 'users', ownerUID, 'reviews', reviewId);
  await updateDoc(reviewRef, reviewData);
};

export const deleteReview = async (ownerUID, reviewId) => {
  const reviewRef = doc(db, 'users', ownerUID, 'reviews', reviewId);
  await deleteDoc(reviewRef);
};

export const addReview = async (ownerUID, reviewData) => {
  const reviewsRef = collection(db, 'users', ownerUID, 'reviews');
  await addDoc(reviewsRef, reviewData);
};

export const getBookingsByCustomerUID = async (customerUID) => {
  const bookingsCol = collection(db, 'bookings');
  const q = query(bookingsCol, 
    where('customerUID', '==', customerUID), 
    where('status', 'in', ['active', 'completed', 'pending']), // Query for what you want, not what you don't
    orderBy('createdAt', 'desc'));
  const querySnapshot = await getDocs(q);
  const bookings = querySnapshot.docs.map((doc) => ({
    ...doc.data(),
    id: doc.id,
  }));
  return bookings;
};

export const deleteMessage = async (messageId) => {
  const messageRef = dbRef(database, `messages/${messageId}`);
  try {
    await remove(messageRef);
  } catch (error) {
    console.error('Error deleting message: ', error);
  }
};

export const archiveMessage = async (messageId) => {
  const messageRef = doc(db, 'messages', messageId);
  try {
    await updateDoc(messageRef, { archived: true });
  } catch (error) {
    console.error('Error archiving message: ', error);
  }
};

export default app;